import type { ModuleSnapshot } from '@ctcells/contract'

import { QUICK_SUPPLY_TARGETS } from '../screens/batches/devQuickBatch.ts'

/**
 * 사전 점검 줄 → 그 자리에서 누를 조치(eric 2026-10-23 「작업 시작 버튼을 누르면.. 지금 사전 점검에서 체크해야 하는 것들을
 * 버튼 형태로 만들어줄래?」 · 「버튼을 만들어서.. 초기화하도록 하는 거 팝업창에서 정리해줘」).
 *
 * | 코드 | 조치 | API · 훅 |
 * | --- | --- | --- |
 * | `HANDOFF_POINT_OCCUPIED_AT_START` | 수거 확인(그 자리 열린 수거 건) · 자리 비움 확약 | `useConfirmUnload` · `useReleaseHandoffPoint` |
 * | `PREFLIGHT_MODULE_NOT_IDLE` | 그 모듈 초기화 · 6축이 쥔 채 `HANDOFF` 면 [작업 중단 후 내려놓기](이 작업 중단 → `place_held` → 같은 내용 재생성 · `HeldTrayFix.tsx`) | `useInitializeModule` · `heldTrayRecreate.ts` |
 * | `PREFLIGHT_TRAY_NOT_LOADED` | 수거 확인(같은 자리 열린 수거 건) · 적재 완료(그 작업 열린 적재 건) | `useConfirmUnload` · `useConfirmLoad` |
 * | `PREFLIGHT_SAFETY_STOP` | 안전 해제 | `useClearSafety` |
 * | `SAFETY_SELFTEST_FAIL` | 정지 풀기(문 열림 등 · 현장 확인 체크 · `StopsReleaseFix.tsx`) — 🔴 `/safety/clear` 는 문 열림을 안 푼다(실측 2026-10-24) | `useReleaseStops` |
 * | `PREFLIGHT_GRIP_DROP_UNRECOVERED` | 파지 기억 초기화(사유) | `useForgetModuleGrips` |
 * | `PREFLIGHT_WASTE_NOT_EMPTIED` | 폐기통 교체 기록 | `useRecordWasteReplacement` |
 * | 팁 · TC 부족 · 시약 여유 없음 | 그 자리 보충 창(링크 아님 · `SupplyFix.tsx`) | `useRecordConsumableReplenishment` · `useRecordReagentReplenishment` |
 * | `PREFLIGHT_CONSUMABLE_UNVERIFIED` | 조치 없음 · 사유만(«못 쟀다» — 팁인지 폐기통인지 줄에 구조화 값이 없다) | — |
 * | `RECIPE_VALIDATION_FAIL` · `RCP-LBW-09` | 설치 정의 넣기(확인 창 — 이 작업 중지 → 설치 정의 PUT) | `useStopBatch` · `useSetInstalledLabware` |
 * | `ALARM_NO_REMOTE_RECIPIENT` · `POSITION_UNTAUGHT` | 조치 없음 · 서버 `action_required` 원문만 | — (그 설정 절이 화면에 없다 · 758b58e) |
 * | `VISION_INSPECT_INCONCLUSIVE_AT_START` | 조치 없음 · 사유만 | — |
 *
 * 🔴 다른 화면으로 가는 링크는 두지 않는다(「화면 어디 들어가서 버튼 누르게 하지 말고」) — 시작 창 위에 대화로 띄운다.
 * 🔴 실재하는 API 만 쓴다. 표에 없는 코드는 조치가 없다(사유 · 코드 원문은 보고서가 그대로 그린다).
 */
export type PreflightFixKind =
  | 'confirmUnload'
  | 'releaseHandoff'
  | 'initialize'
  | 'confirmLoad'
  | 'clearSafety'
  | 'forgetGrips'
  | 'replaceWaste'
  | 'replenish'
  | 'installLabware'
  | 'releaseStops'
  | 'placeHeldRecreate'

const BY_CODE: Readonly<Record<string, readonly PreflightFixKind[]>> = {
  HANDOFF_POINT_OCCUPIED_AT_START: ['confirmUnload', 'releaseHandoff'],
  PREFLIGHT_MODULE_NOT_IDLE: ['initialize', 'placeHeldRecreate'],
  PREFLIGHT_TRAY_NOT_LOADED: ['confirmUnload', 'confirmLoad'],
  PREFLIGHT_SAFETY_STOP: ['clearSafety'],
  SAFETY_SELFTEST_FAIL: ['releaseStops'],
  PREFLIGHT_GRIP_DROP_UNRECOVERED: ['forgetGrips'],
  PREFLIGHT_WASTE_NOT_EMPTIED: ['replaceWaste'],
  TIP_SHORTAGE_AT_START: ['replenish'],
  TC_SHORTAGE_AT_START: ['replenish'],
  TIP_STOCK_LOW: ['replenish'],
  REAGENT_VOLUME_LOW: ['replenish'],
}

/** 폐기통 설치 정의가 없는 줄 — 그 자리에 설치 정의를 넣는다(`InstalledLabwareFix.tsx`) */
const INSTALL_LABWARE_ITEMS: ReadonlySet<string> = new Set(['RCP-LBW-09'])

/** 그 줄에서 누를 조치. 없으면 빈 목록 — 조치를 지어내지 않는다 */
export function preflightFixesFor(code: string, item: string | null): readonly PreflightFixKind[] {
  /* 🔴 `item` 은 규칙 ID 로 «시작하는» 한 줄이다(실측 2026-10-23 「RCP-LBW-09 staining.waste_container: 설치 정의 사본 없음 — …」) — 첫 낱말로 본다 */
  const rule = item?.split(' ')[0]
  if (code === 'RECIPE_VALIDATION_FAIL' && rule !== undefined && INSTALL_LABWARE_ITEMS.has(rule))
    return ['installLabware']
  return BY_CODE[code] ?? []
}

/** 줄 하나를 가리키는 열쇠 — 같은 코드가 모듈 · 자리마다 여러 줄로 온다 */
export function preflightRowKey(row: {
  code: string
  module_id: string | null
  item: string | null
}): string {
  return `${row.code}|${row.module_id ?? ''}|${row.item ?? ''}`
}

/** 소모품 줄의 보충 창 종류 — `SupplyFix.tsx` 가 그린다 */
export type SupplyFixKind = 'tip' | 'tc' | 'reagent'

/** 그 줄의 모듈 종류 → 채울 것. 시약 줄은 코드가 정한다 */
export function supplyKindOf(
  code: string,
  module: ModuleSnapshot | undefined,
): SupplyFixKind | undefined {
  if (code === 'REAGENT_VOLUME_LOW') return 'reagent'
  const target = QUICK_SUPPLY_TARGETS.find((one) => one.moduleType === module?.module_type)
  return target?.kind
}
